import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import { PageHeader } from "./layoutChrome";
import { FileText, Users, ClipboardList, KeyRound, BookOpen, Lightbulb, Download, ExternalLink, Briefcase, MessageSquare, FolderOpen } from "lucide-react";
import { useWorkspaceMode } from "@/lib/workspaceMode";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { LICENSE_INSTALL_NOTICE_TITLE, LICENSE_INSTALL_NOTICE_PARAS } from "@/lib/licenseInstallNotice";
import {
  WORK_MODULES,
  HUB_TRAMITES,
  MODULE_CARDS,
  VISOR_MODULE_CARD_TITLE,
  getPanelCardsForUser,
} from "@/modules/workModuleCore";
import { CONFIGURACION_EMISOR_PATH, buildConfiguracionRenovacionUrl } from "@/modules/config/configuracionRoutes";
import { useEntitlements } from "@/modules/useEntitlements";
import { resolveHubModuleAccess } from "@/modules/moduleAccess";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";
import { useCommercialSubmissionGate } from "@/lib/useCommercialSubmissionGate";
import {
  HUB_MODULE_ACTION,
  resolveHubModuleCommercialAction,
} from "@/lib/hubModuleCommercialState";
import { EXPANSION_KIND_MODULE } from "@/lib/commercialLicenseExpansion";
import { licenseRequestStatusLabel } from "@/lib/commercialLicenseRequests";
import HubModuleCard from "@/components/HubModuleCard";
import { enterWorkModuleFromHub } from "@/lib/enterWorkModule";
import ModulePlanBlockedDialog from "@/components/ModulePlanBlockedDialog";
import { resolvePanelHubEntry } from "@/modules/panelHubEntry";
import { EQUIPO_CHAT_PATH } from "@/modules/equipo/equipoRoutes";
import { buildCommonToolPathWithReturn, buildCommonToolReturnState } from "@/lib/equipoNavigation";
import EquipoBasicoUsuarios from "@/pages/equipo/EquipoBasicoUsuarios";
import AssignedTasksPanel from "@/components/equipo/AssignedTasksPanel";
import { openBntAsistente } from "@/modules/asistente/bntAsistenteRoutes";
import { useDespachoPanelVisible } from "@/modules/despacho/useDespachoPanelVisible";
import { canManageWorkspaceTeam } from "@/lib/auxiliaryRoleGuide";
import {
  PANEL_GENERAL_TABS,
  getPanelGeneralTabs,
  resolvePanelGeneralTab,
} from "@/modules/panelGeneralTabs";
import DatosMaestrosHubContent from "@/components/datosMaestros/DatosMaestrosHubContent";
import PanelHerramientasContent from "@/components/panel/PanelHerramientasContent";
import { useBuyerModuleVisibility } from "@/lib/useBuyerModuleVisibility";
import { isBuyerModuleVisibleToUser } from "@/lib/buyerModuleVisibility";

const CARD_ICONS = {
  [WORK_MODULES.VISOR_SAT]: FileText,
  [WORK_MODULES.ASISTENTE_LABORAL]: Users,
  [WORK_MODULES.TRAMITES_FISCALES]: ClipboardList,
  [HUB_TRAMITES]: ClipboardList,
  [WORK_MODULES.GESTION_DESPACHO]: Briefcase,
  [WORK_MODULES.MATERIALIDAD]: FolderOpen,
  [WORK_MODULES.DISENADOR]: KeyRound,
};

/**
 * Panel General (Hub) — bienvenida tras login. Cliente y licencia en la barra superior (Layout).
 */
export default function PanelGeneral() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { mode, canSwitch, setMode } = useWorkspaceMode();
  const isPlatformDesignerUser = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const { modules: buyerModuleVisibility } = useBuyerModuleVisibility();
  const {
    modulo_visor_sat,
    modulo_asistente_laboral,
    modulo_tramites_fiscales,
    modulo_gestion_despacho,
    entitlements,
    systemInfo,
    loading: permLoading,
    isPlatformDesigner: entitlementDesignerBypass,
  } = useEntitlements();
  const despachoModuleActive = useDespachoPanelVisible();
  const isWorkspaceTeamAdmin = canManageWorkspaceTeam(user);
  const showBuyerEquipoSection =
    Boolean(user?.is_admin_owner || (user?.is_buyer_tenant && user?.role === "buyer_admin") || isWorkspaceTeamAdmin)
    || (isPlatformDesignerUser && buyerPreviewActive);
  const buyerPreviewMode = Boolean(isPlatformDesignerUser && buyerPreviewActive);
  const { gate: commercialGate } = useCommercialSubmissionGate(user, !buyerPreviewMode);
  const distributorEmisor = Boolean(
    !isPlatformDesignerUser && canSwitch && mode === "distribuidor" && user?.can_generate_licenses,
  );

  const [licenseInstallNoticeOpen, setLicenseInstallNoticeOpen] = useState(false);
  const [planBlockedOpen, setPlanBlockedOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const showHerramientasSection = Boolean(
    (
      user?.is_admin_owner
      || (user?.is_buyer_tenant && user?.role === "buyer_admin")
      || isWorkspaceTeamAdmin
      || (isPlatformDesignerUser && !buyerPreviewActive)
    )
    && mode !== "distribuidor",
  );

  const panelTabs = useMemo(
    () =>
      getPanelGeneralTabs({
        mode,
        showEquipo: showBuyerEquipoSection,
        showHerramientas: showHerramientasSection,
      }),
    [mode, showBuyerEquipoSection, showHerramientasSection],
  );

  const activePanelTab = resolvePanelGeneralTab(searchParams.get("panel"), panelTabs);

  const setActivePanelTab = (tabId) => {
    const next = new URLSearchParams(searchParams);
    if (tabId === PANEL_GENERAL_TABS.MODULOS) {
      next.delete("panel");
    } else {
      next.set("panel", tabId);
    }
    setSearchParams(next, { replace: true });
  };

  const openAssistantWindow = () => {
    openBntAsistente();
  };

  useEffect(() => {
    const onNotice = () => setLicenseInstallNoticeOpen(true);
    window.addEventListener("visor:license-install-notice", onNotice);
    return () => window.removeEventListener("visor:license-install-notice", onNotice);
  }, []);

  const hubCards = getPanelCardsForUser({ isPlatformDesigner: isPlatformDesignerUser });

  const accessCtx = {
    isPlatformDesigner: entitlementDesignerBypass,
    permLoading,
    modulo_visor_sat,
    modulo_asistente_laboral,
    modulo_tramites_fiscales,
    modulo_gestion_despacho,
    entitlements,
  };

  const hasModuleAccessForCard = (cardId) => resolveHubModuleAccess(cardId, accessCtx);

  const visibleHubCards = hubCards.filter((cardId) => {
    if (
      !isBuyerModuleVisibleToUser(cardId, {
        modules: buyerModuleVisibility,
        isPlatformDesigner: isPlatformDesignerUser,
        buyerPreviewActive,
      })
    ) {
      return false;
    }
    const meta = MODULE_CARDS[cardId];
    if (meta?.hideWhenNotEntitled) {
      const access = hasModuleAccessForCard(cardId);
      if (access === false) return false;
    }
    return true;
  });

  const enterModule = (cardId) => {
    const hasAccess = hasModuleAccessForCard(cardId);
    // Mientras cargan entitlements, no navegar a solicitud comercial.
    if (hasAccess == null || permLoading) return;

    const commercial = resolveHubModuleCommercialAction(cardId, {
      hasModuleAccess: hasAccess,
      systemInfo,
      gate: commercialGate,
    });

    if (commercial.action === HUB_MODULE_ACTION.REQUEST_MODULE) {
      navigate(
        buildConfiguracionRenovacionUrl({
          expansionType: EXPANSION_KIND_MODULE,
          moduleId: commercial.commercialModuleId,
        }),
      );
      return;
    }

    if (commercial.action === HUB_MODULE_ACTION.PENDING) {
      navigate(buildConfiguracionRenovacionUrl());
      return;
    }

    const entry = resolvePanelHubEntry(cardId, accessCtx);
    if (!entry.allowed) {
      if (entry.reason === "no_entitlement") {
        setPlanBlockedOpen(true);
      }
      return;
    }
    enterWorkModuleFromHub({
      cardId,
      navigate,
      canSwitch,
      setWorkspaceMode: setMode,
      isPlatformDesigner: isPlatformDesignerUser,
    });
  };

  return (
    <>
      <PageHeader
        compact
        title="Panel General"
        subtitle="Instalación, licencia, cliente predeterminado y acceso a cada módulo."
      />

      <div className="p-8 space-y-8 w-full max-w-none">
        {isPlatformDesignerUser && !buyerPreviewActive ? (
          <div
            className="rounded-sm border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-950 flex flex-wrap items-center gap-2"
            data-testid="platform-designer-badge"
          >
            <span className="inline-flex items-center rounded-sm bg-[#0F2D5C] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              Plataforma BNT
            </span>
            <span>
              <span className="font-semibold">Modo Plataforma BNT</span>
              {" — "}
              Superusuario con acceso global a clientes y módulos, sin flujo distribuidor ni comprador.
            </span>
          </div>
        ) : null}
        {!isPlatformDesignerUser && canSwitch && mode === "distribuidor" ? (
          <div className="rounded-sm border border-violet-300 bg-violet-50 px-4 py-3 text-sm text-violet-950">
            <span className="font-semibold">Modo Distribuidor</span>
            {" — "}
            Vista de emisión. Para licencias:{" "}
            <Link to={CONFIGURACION_EMISOR_PATH} className="underline font-medium">Gestor de licencias</Link>.
          </div>
        ) : null}

        <Tabs
          value={activePanelTab}
          onValueChange={setActivePanelTab}
          className="space-y-6"
          data-testid="panel-general-tabs"
        >
          <div
            className="sticky top-0 z-10 -mx-8 px-8 py-2 bg-[#F4F6F8]/95 backdrop-blur-sm border-b border-slate-200/90"
            data-testid="panel-general-tabs-bar"
          >
            <TabsList className="w-full h-auto min-h-9 flex flex-wrap justify-start gap-1 rounded-sm bg-white border border-slate-200 p-1 shadow-sm">
              {panelTabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="rounded-sm text-xs sm:text-sm px-3 py-1.5 data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white shrink-0"
                  data-testid={tab.testId}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value={PANEL_GENERAL_TABS.MODULOS} className="mt-0 space-y-8 focus-visible:outline-none">
        <section aria-label="Módulos disponibles">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2D5C]">Módulos</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
            {visibleHubCards.map((cardId) => {
              const meta = MODULE_CARDS[cardId];
              if (!meta) return null;
              const Icon = CARD_ICONS[cardId] || FileText;
              const hasModuleAccess = hasModuleAccessForCard(cardId);
              const commercial = resolveHubModuleCommercialAction(cardId, {
                hasModuleAccess,
                systemInfo,
                gate: commercialGate,
              });
              const pendingNote =
                commercial.action === HUB_MODULE_ACTION.PENDING && commercialGate?.status
                  ? `Solicitud ${licenseRequestStatusLabel(commercialGate.status)} en Centro SaaS.`
                  : null;
              return (
                <HubModuleCard
                  key={cardId}
                  meta={meta}
                  icon={Icon}
                  hasModuleAccess={hasModuleAccess}
                  accessLoading={permLoading}
                  onEnter={() => enterModule(cardId)}
                  testId={`hub-card-${cardId}`}
                  actionLabel={commercial.label}
                  actionDisabled={commercial.disabled}
                  actionVariant={
                    commercial.action === HUB_MODULE_ACTION.REQUEST_MODULE
                      ? "request"
                      : commercial.action === HUB_MODULE_ACTION.PENDING
                        ? "pending"
                        : "primary"
                  }
                  pendingNote={pendingNote}
                />
              );
            })}
          </div>
        </section>

        <section className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-hidden">
          <Accordion type="single" collapsible>
            <AccordionItem value="primeros-pasos" className="border-0">
              <AccordionTrigger className="px-5 py-4 hover:no-underline" data-testid="primeros-pasos-trigger">
                <span className="flex items-center gap-2 text-sm font-semibold text-[#0F2D5C]">
                  <Lightbulb className="w-4 h-4 text-[#3DA873]" />
                  Primeros Pasos y Recomendaciones y Manual de usuario
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5 pt-0 text-sm text-slate-700 space-y-4">
                <div>
                  <h4 className="font-semibold text-[#0F2D5C] text-xs uppercase tracking-wide mb-2">Flujo recomendado</h4>
                  <ol className="list-decimal pl-5 space-y-1.5 text-xs leading-relaxed">
                    <li>Registre contribuyentes en{" "}
                      <Link to="/?panel=datos-maestros" className="text-[#0F2D5C] underline font-medium">
                        Datos Maestros → Empresas / Clientes
                      </Link>.
                    </li>
                    <li>Entre al módulo deseado y <strong>seleccione el cliente de trabajo</strong> en la barra superior.</li>
                    <li>Cargue XML o ejecute descarga SAT; luego use Confronta y visores para validar.</li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F2D5C] text-xs uppercase tracking-wide mb-2">Consejos operativos</h4>
                  <ul className="list-disc pl-5 space-y-1 text-xs leading-relaxed">
                    <li>Cada módulo recuerda su propio cliente activo ({VISOR_MODULE_CARD_TITLE}, Laboral y Trámites son independientes).</li>
                    <li>Configure FIEL por cliente antes de la descarga automática SAT.</li>
                    <li>Revise licencia y periodo de prueba en la barra de instalación arriba.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F2D5C] text-xs uppercase tracking-wide mb-2">Manual de usuario</h4>
                  <p className="text-xs leading-relaxed mb-2 text-slate-600">
                    Guía de uso del sistema: clientes, carga y confronta CFDI, Asistente Laboral/REPSE, licencias y respaldos.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/ayuda"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0F2D5C] underline"
                      data-testid="hub-manual-ayuda-link"
                    >
                      <BookOpen className="w-3.5 h-3.5" />
                      Ver en Ayuda y Manuales
                    </Link>
                    <a
                      href={`${API}/docs/manual-uso`}
                      download="Manual_Uso_VisorFiscal.pdf"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0F2D5C] underline"
                      data-testid="hub-manual-download-link"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Descargar PDF
                    </a>
                    <a
                      href={`${API}/docs/manual-uso`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0F2D5C] underline"
                      data-testid="hub-manual-open-link"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Abrir PDF
                    </a>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
          </TabsContent>

          <TabsContent value={PANEL_GENERAL_TABS.DATOS_MAESTROS} className="mt-0 focus-visible:outline-none">
            <section aria-label="Datos maestros del espacio de trabajo" data-testid="panel-datos-maestros">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2D5C]">Datos Maestros</h2>
                <span className="text-[11px] text-slate-500">Catálogo fiscal compartido · todos los módulos</span>
              </div>
              <DatosMaestrosHubContent embedded />
            </section>
          </TabsContent>

          <TabsContent value={PANEL_GENERAL_TABS.EQUIPO} className="mt-0 focus-visible:outline-none">
            <section aria-label="Actividades de equipo" data-testid="panel-equipo-auxiliares">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2D5C]">Actividades de Equipo</h2>
                <span className="text-[11px] text-slate-500">Usuarios · permisos Auxiliar vs Administrador</span>
              </div>
              <EquipoBasicoUsuarios embedded />
            </section>

            <section
              aria-label="Tareas asignadas al equipo"
              className="mt-6 pt-6 border-t border-slate-200"
              data-testid="panel-equipo-assigned-tasks"
            >
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2D5C]">
                  Tareas asignadas al equipo
                </h2>
                <span className="text-[11px] text-slate-500">
                  Sincronizado con BNT Asistente · control y reportes
                </span>
              </div>
              <AssignedTasksPanel variant="equipo" user={user} />
            </section>
          </TabsContent>

          <TabsContent value={PANEL_GENERAL_TABS.COMUNICACION} className="mt-0 focus-visible:outline-none">
            <section aria-label="Equipo y comunicación" data-testid="panel-equipo-comunicacion">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2D5C]">BNT Asistente y chat</h2>
                <span className="text-[11px] text-slate-500">Chat y mensajes en BNT Asistente (ventana flotante)</span>
              </div>
              <div
                className="rounded-md border border-slate-200 bg-white shadow-sm p-4 space-y-3"
                data-testid="panel-equipo-comunicacion-hub"
              >
                <p className="text-xs text-slate-600 m-0 leading-relaxed max-w-2xl">
                  Use <strong>BNT Asistente</strong> para chat interno del equipo, atención a clientes y alertas
                  operativas sin saturar el panel de módulos. También puede abrir el chat en pantalla completa.
                </p>
                <div className="flex flex-wrap items-center gap-2" data-testid="panel-equipo-comunicacion-actions">
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 rounded-sm text-xs bg-[#0F2D5C] hover:bg-[#0F2D5C]/90 shrink-0"
                    onClick={openAssistantWindow}
                    data-testid="hub-bnt-asistente-btn"
                  >
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" aria-hidden />
                    Abrir BNT Asistente
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-sm text-xs shrink-0"
                    onClick={() =>
                      navigate(buildCommonToolPathWithReturn(EQUIPO_CHAT_PATH, "/"), {
                        state: buildCommonToolReturnState("/", {
                          returnLabel: "Volver al Panel General",
                          sourceModule: "panel_general",
                        }),
                      })
                    }
                    data-testid="hub-chat-interno-full-btn"
                  >
                    Chat interno (pantalla completa)
                  </Button>
                </div>
              </div>
            </section>
          </TabsContent>

          <TabsContent value={PANEL_GENERAL_TABS.HERRAMIENTAS} className="mt-0 focus-visible:outline-none">
            <PanelHerramientasContent
              isPlatformDesigner={isPlatformDesignerUser}
              buyerPreviewActive={buyerPreviewActive}
            />
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={licenseInstallNoticeOpen} onOpenChange={setLicenseInstallNoticeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{LICENSE_INSTALL_NOTICE_TITLE}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-700">
                {LICENSE_INSTALL_NOTICE_PARAS.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-[#0F2D5C]">Entendido</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ModulePlanBlockedDialog open={planBlockedOpen} onOpenChange={setPlanBlockedOpen} />
    </>
  );
}
